import { useEffect, useState } from "react";
import {
  canShowInstall,
  isStandalone,
  onInstallAvailabilityChange,
  promptInstall
} from "../lib/pwa";

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isDesktop(): boolean {
  if (typeof navigator === "undefined") return true;
  return !/android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

type Props = {
  alwaysShow?: boolean;
  className?: string;
};

export function InstallAppButton({ alwaysShow = true, className = "" }: Props) {
  const [canInstall, setCanInstall] = useState(canShowInstall());
  const [installed, setInstalled] = useState(isStandalone());
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setInstalled(isStandalone());
    return onInstallAvailabilityChange(setCanInstall);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  if (installed) {
    return (
      <button type="button" className={`btn ghost sm install-btn installed ${className}`} disabled title="Running as installed app">
        ✓ Installed
      </button>
    );
  }

  if (!canInstall && !alwaysShow) return null;

  async function handleClick() {
    if (canInstall) {
      setBusy(true);
      const outcome = await promptInstall();
      setBusy(false);
      if (outcome === "accepted") {
        setToast("Installed — open CRM Autodialer from your desktop or app list");
        setInstalled(true);
      } else if (outcome === "dismissed") {
        setToast("Install cancelled");
      }
      return;
    }
    setHelpOpen(true);
  }

  return (
    <>
      <button
        type="button"
        className={`btn ghost sm install-btn ${className}`}
        onClick={() => void handleClick()}
        disabled={busy}
        title="Install as desktop or home-screen app"
      >
        {busy ? "Installing…" : "Install app"}
      </button>

      {toast ? <div className="pwa-toast">{toast}</div> : null}

      {helpOpen ? (
        <div className="pwa-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pwa-install-title">
          <div className="pwa-modal">
            <h2 id="pwa-install-title">Install CRM Autodialer</h2>
            <p className="muted">
              Add a desktop or home-screen app that opens like native software (standalone window).
            </p>
            {isDesktop() ? (
              <ol className="pwa-steps">
                <li>
                  <strong>Chrome / Edge / Brave:</strong> open the browser menu (⋮) → <em>Install Falcon CRM Autodialer</em> or
                  use the install icon in the address bar.
                </li>
                <li>
                  <strong>Microsoft Edge:</strong> Apps → Install this site as an app.
                </li>
                <li>
                  After install, launch from the Start menu, desktop shortcut, or taskbar.
                </li>
              </ol>
            ) : isIos() ? (
              <ol className="pwa-steps">
                <li>Tap the Share button in Safari.</li>
                <li>Choose <strong>Add to Home Screen</strong>.</li>
                <li>Confirm <strong>Add</strong> — the app opens full-screen.</li>
              </ol>
            ) : (
              <ol className="pwa-steps">
                <li>Open the browser menu.</li>
                <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
                <li>Open CRM Autodialer from your app drawer.</li>
              </ol>
            )}
            <div className="row-actions" style={{ marginTop: "1rem" }}>
              <button type="button" className="btn" onClick={() => setHelpOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
